import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, Droplets, Beaker, TrendingUp, ArrowDown, ExternalLink, AlertTriangle, Lightbulb, Target, Zap } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import CropCTASection from '../components/CropCTASection';

const TomatoPage = () => {

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Tomato Success: Mastering pH for Maximum Yield"
        description="Research-backed pH management for profitable tomato production. Learn how water quality affects your tomato crop yield and quality."
        canonicalPath="/tomato"
      />
      {/* Navbar */}
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-screen md:min-h-[70vh] flex items-center justify-center overflow-hidden pt-16 pb-8">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src="/Tomatos.jpg"
            alt="Tomato plants with ripe red tomatoes"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-red-900/80 via-red-800/70 to-black/60"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center py-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-4 md:mb-6"
            >
              <span className="text-xl">🍅</span>
              <span>High-Value Crop Resource</span>
            </motion.div>

            <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold mb-4 md:mb-6 leading-tight">
              Tomato Success:
              <span className="block text-red-300">Mastering pH for Maximum Yield</span>
            </h1>
            
            <p className="text-lg md:text-xl lg:text-2xl mb-6 md:mb-8 text-white/90 leading-relaxed max-w-4xl mx-auto">
              Research-backed pH management for profitable tomato production
            </p>

            <motion.button
              onClick={() => document.getElementById('intro-content')?.scrollIntoView({ behavior: 'smooth' })}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-red-500 text-white px-8 md:px-12 py-3 md:py-4 rounded-lg font-bold text-lg md:text-xl hover:bg-red-600 transition-colors inline-flex items-center space-x-3 shadow-lg mb-6 md:mb-8"
            >
              <span>Start Reading Guide</span>
              <ArrowDown size={20} className="md:w-6 md:h-6" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Intro Content */}
      <section id="intro-content" className="py-16 md:py-20 bg-gradient-to-br from-red-50 to-orange-50">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center space-x-2 bg-red-100 text-red-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <span className="text-xl">🍅</span>
              <span>Introduction</span>
            </div>
            
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-red-200">
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
                <strong>Tomatoes are sensitive to soil pH and water quality</strong> — and your irrigation water could be quietly undermining your crop's productivity and fruit quality season after season.
              </p>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed mt-4">
                At CTC Waterworks, we've helped vegetable growers optimize their water quality for 40+ years. This guide explains why your irrigation water pH matters for tomatoes, what research shows, and how sulfur burners can protect your soil, enhance your fruit quality, and maximize your profits.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Research-Based Content Sections */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 space-y-16">
          
          {/* Section 1: Ideal Soil pH for Tomatoes */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🔍</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                1: Ideal Soil pH for Tomatoes
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-6 md:p-8 border border-red-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-red-500 pl-6">
                "Tomatoes grow best in slightly acidic soils with pH between 6.0 and 6.8. When soil pH drifts higher, micronutrient availability — especially iron and manganese — drops, resulting in chlorosis and poor fruit development."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-red-600" size={16} />
                <span className="text-sm font-medium text-red-600">
                  Source: University of California Agriculture & Natural Resources — Soil Management in Vegetable Production
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <Target className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">If your irrigation water is high in bicarbonates, it can raise soil pH over time — locking up nutrients that tomatoes need for balanced vine growth and uniform fruit ripening.</p>
              </div>
            </div>
          </motion.div>

          {/* Section 2: Hidden Problem - Bicarbonates in Irrigation */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">💧</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                2: Hidden Problem — Bicarbonates in Irrigation
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 md:p-8 border border-orange-200">
              <p className="text-lg md:text-xl text-gray-700 mb-6 leading-relaxed">
                Think of bicarbonates as tiny pH "elevators" in your water. When you irrigate with high-bicarbonate water, 
                these compounds slowly but steadily raise soil pH. It's like adding lime to your 
                tomato field, drop by drop, season after season. Even if your soil pH starts in the optimal range, 
                alkaline irrigation water can gradually push it above the sweet spot for tomato production.
              </p>

              <blockquote className="text-lg text-gray-700 mb-6 italic border-l-4 border-orange-500 pl-6 bg-white rounded-lg p-4">
                "High bicarbonate water can gradually increase soil pH, causing infiltration problems and nutrient tie-up in vegetable crops like tomatoes."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-orange-600" size={16} />
                <span className="text-sm font-medium text-orange-600">
                  Source: Penn State Extension — Irrigation Water Quality for Greenhouses & Vegetables
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">Tomato growers should test irrigation water for bicarbonates each season. If levels are above ~120 ppm, inline acidification can protect your root zone and your yields.</p>
              </div>
            </div>
          </motion.div>

          {/* Section 3: Impact on Drip Irrigation */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🚜</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                3: Impact on Drip Irrigation
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 md:p-8 border border-blue-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-blue-500 pl-6">
                "Precipitation of carbonates and bicarbonates can lead to scaling inside pipelines and drip emitters, reducing water distribution uniformity."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-blue-600" size={16} />
                <span className="text-sm font-medium text-blue-600">
                  Source: UC ANR — Microirrigation for Vegetable Crops
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <Zap className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">Clogged emitters mean some plants get too little or too much water — resulting in uneven fruit size and quality. Inline acidification helps prevent scale buildup before it starts.</p>
              </div>

              {/* Problem vs Solution Visual */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <h4 className="font-semibold text-red-800 mb-3">❌ High-Bicarbonate Water Problems:</h4>
                  <ul className="space-y-2 text-sm text-red-700">
                    <li>• Clogged drip emitters</li>
                    <li>• Uneven water distribution</li>
                    <li>• Variable fruit size</li>
                    <li>• Inconsistent ripening</li>
                  </ul>
                </div>
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-3">✅ With pH Management:</h4>
                  <ul className="space-y-2 text-sm text-green-700">
                    <li>• Clean, efficient emitters</li>
                    <li>• Uniform water application</li>
                    <li>• Consistent fruit sizing</li>
                    <li>• Even ripening patterns</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Section 4: Nutrient Uptake and Fruit Quality */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🌱</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                4: Nutrient Uptake and Fruit Quality
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-6 md:p-8 border border-green-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-green-500 pl-6">
                "Maintaining optimal root zone pH helps maximize nutrient availability and improves fruit quality in tomato crops."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-green-600" size={16} />
                <span className="text-sm font-medium text-green-600">
                  Source: University of Florida IFAS Extension — Tomato Production in Florida
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <Lightbulb className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">When you manage irrigation water pH properly, you keep iron and micronutrients available — supporting strong fruit set and higher marketable yields.</p>
              </div>

              {/* Quality Factors Visual */}
              <div className="mt-6 bg-white rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">How Water pH Affects Tomato Quality:</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                    <h5 className="font-semibold text-red-800 mb-2">Color Development</h5>
                    <p className="text-sm text-gray-700">Proper pH ensures micronutrients needed for lycopene production are available</p>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                    <h5 className="font-semibold text-red-800 mb-2">Fruit Firmness</h5>
                    <p className="text-sm text-gray-700">Balanced calcium uptake improves cell wall strength and shelf life</p>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                    <h5 className="font-semibold text-red-800 mb-2">Flavor Profile</h5>
                    <p className="text-sm text-gray-700">Optimal nutrient balance enhances sugar-acid ratio and flavor compounds</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Section 5: Bicarbonate Threshold */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">📊</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                5: Bicarbonate Threshold
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 md:p-8 border border-purple-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-purple-500 pl-6">
                "When bicarbonate levels in irrigation water exceed 120 ppm, acidification is generally recommended to maintain soil and water pH within the crop's ideal range."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-purple-600" size={16} />
                <span className="text-sm font-medium text-purple-600">
                  Source: University of Arizona Cooperative Extension — Salinity and Agriculture
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500 mb-6">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">Neutralizing bicarbonates before they enter your soil keeps your drip system flowing freely and your root zone stable — so your tomato crop stays healthy from transplant to final pick.</p>
              </div>
              
              <div className="bg-white rounded-lg p-6">
                <h4 className="text-xl font-semibold text-gray-900 mb-4">Why Choose CTC Sulfur Burners for Tomatoes:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    'Supports root-zone pH stability by neutralizing bicarbonates in irrigation water',
                    'Prevents emitter clogging in drip systems',
                    'Safer than handling liquid acids',
                    'Provides sulfur nutrition as a bonus',
                    'Automated operation with minimal maintenance',
                    '40+ years of proven results in vegetable crops'
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <CheckCircle className="text-green-500 flex-shrink-0" size={16} />
                      <span className="text-gray-700 text-sm">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <CropCTASection cropName="Tomatoes" />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default TomatoPage;